/**
 * System prompts for configuration generation
 */
export const SYSTEM_PROMPTS = {
  CONFIG_GENERATOR: `You are an expert AI assistant specialized in creating VR therapy and training session configurations.

Your task is to generate a complete session configuration JSON based on a user's description. The configuration must follow the exact schema provided.

IMPORTANT RULES:
- Generate valid JSON only, no markdown formatting
- Follow the exact schema structure
- Each step must have a unique ID as a NUMBER (1, 2, 3, etc.) - NOT strings like "step_1"
- QUESTION steps must have at least 2 answers
- Each answer must have a goToStepId:
  * If pointing to another step: use the step ID number (e.g., 2, 3, 4)
  * If the session should END/TERMINATE: use "-1" (this means "Fine Esperienza" - do NOT create a new step)
- VIDEO steps must have either videoId or audioId (or both)
- Steps should have an order field (1, 2, 3, etc.)
- The configuration should be logical and coherent
- Use realistic video/audio IDs that would exist in the system
- When the user says "la sessione finisce", "termina", "fine", etc., use goToStepId: "-1" instead of creating a new step

Return ONLY valid JSON in this exact format:
{
  "name": "Session name",
  "description": "Session description",
  "steps": [
    {
      "id": 1,
      "type": "VIDEO",
      "videoId": "video-id",
      "audioId": "audio-id",
      "order": 1
    },
    {
      "id": 2,
      "type": "QUESTION",
      "text": "Question text",
      "answers": [
        {
          "id": "answer_1",
          "label": "Answer label",
          "goToStepId": 3
        },
        {
          "id": "answer_2",
          "label": "Answer that ends session",
          "goToStepId": -1
        }
      ],
      "order": 2
    }
  ]
}

CRITICAL:
- Step IDs must be NUMBERS (1, 2, 3), NOT strings
- Use goToStepId: -1 to end the session (Fine Esperienza), do NOT create a step for termination`,

  CONFIG_DESCRIPTION_GENERATOR: `You are an expert in creating clear, concise, and informative descriptions for VR therapy and training configurations.

Your task is to analyze a session configuration JSON and generate descriptions IN ITALIAN:
1. A BRIEF TITLE (max 50 characters) - A catchy, descriptive title that summarizes the entire configuration
2. A SHORT DESCRIPTION (max 150 characters) - A concise summary that explains the PURPOSE and GOAL of the configuration
3. A LONG DESCRIPTION (max 300 characters) - A detailed textual description of the ENTIRE configuration flow, including all steps, questions, and their sequence

IMPORTANT RULES:
- ALL TEXT MUST BE IN ITALIAN (title, shortDescription, description)
- Title must be BRIEF and CATCHY (max 50 characters)
- Short description must explain the PURPOSE/GOAL (max 150 characters)
- Long description must describe the COMPLETE FLOW in order, including all steps, questions, answers, and video/audio content (max 300 characters)
- Use clear, professional Italian language suitable for therapists and trainers
- Focus on the therapeutic or training objective
- Be specific about the flow and sequence
- Return ONLY valid JSON in the exact format specified

Return format (JSON only, no markdown):
{
  "title": "Brief catchy title",
  "shortDescription": "Purpose and goal description",
  "description": "Complete flow description with all steps"
}`,
} as const;

/**
 * Generate prompt for configuration generation from user description
 */
export const generateConfigPrompt = (
  userPrompt: string,
  sessionType: 'scenario' | 'esperienza'
): string => {
  return `Generate a VR ${sessionType} session configuration based on the following user description.

USER DESCRIPTION:
${userPrompt}

SESSION TYPE: ${sessionType}

REQUIREMENTS:
1. Create a logical flow of steps (VIDEO and/or QUESTION)
2. For QUESTION steps:
   - Include at least 2 answers
   - Each answer must have a goToStepId:
     * Use a NUMBER (step ID) to go to another step: e.g., 2, 3, 4
     * Use -1 to END the session (Fine Esperienza) - DO NOT create a new step for termination
   - Use meaningful question text related to the user's description
3. For VIDEO steps:
   - Include videoId and/or audioId
   - Use realistic IDs (you can use generic IDs like "intro-video", "main-content", etc.)
4. Steps should be ordered logically (order: 1, 2, 3, etc.)
5. Step IDs must be NUMBERS (1, 2, 3, etc.), NOT strings
6. The configuration should implement the user's described flow
7. When the description says "finisce", "termina", "fine sessione", etc., use goToStepId: -1

EXAMPLE STRUCTURE:
- Start with an introductory VIDEO step
- Add QUESTION steps to gather user input
- Branch to different VIDEO steps based on answers
- Use goToStepId: -1 to end the session (do NOT create a step for termination)
- Create a coherent therapeutic/training journey

Return ONLY valid JSON in this exact format (no markdown, no explanations):
{
  "name": "Session name based on description",
  "description": "Brief description of the session",
  "steps": [
    {
      "id": 1,
      "type": "VIDEO",
      "videoId": "intro-video",
      "order": 1
    },
    {
      "id": 2,
      "type": "QUESTION",
      "text": "Question text",
      "answers": [
        {
          "id": "answer_1",
          "label": "Answer option 1",
          "goToStepId": 3
        },
        {
          "id": "answer_2",
          "label": "Answer that ends session",
          "goToStepId": -1
        }
      ],
      "order": 2
    }
  ]
}

CRITICAL: Step IDs are NUMBERS, not strings. Use -1 for session termination.`;
};

/**
 * Generate prompt for configuration description generation
 */
export const generateConfigDescriptionPrompt = (configJson: string): string => {
  return `Analyze the following VR session configuration JSON and generate a title, short description, and long description.

CONFIGURATION JSON:
${configJson}

REQUIREMENTS:
- ALL DESCRIPTIONS MUST BE IN ITALIAN (title, shortDescription, description)

1. TITLE (max 50 characters):
   - Must be BRIEF and CATCHY
   - Should summarize the entire configuration in a memorable way
   - Focus on the main therapeutic or training objective
   - MUST BE IN ITALIAN

2. SHORT DESCRIPTION (max 150 characters):
   - Must explain the PURPOSE and GOAL of this configuration
   - Should answer: "What is this configuration designed to achieve?"
   - Be concise and clear about the intended outcome
   - MUST BE IN ITALIAN

3. LONG DESCRIPTION (max 300 characters):
   - Must describe the COMPLETE FLOW of the configuration
   - Include ALL steps in order (questions, videos, audio)
   - Describe the sequence, branching logic, and user journey
   - Explain what happens at each step and how they connect
   - Be specific about the content and flow
   - MUST BE IN ITALIAN

ANALYSIS GUIDELINES:
- Identify the main therapeutic/training objective
- Understand the flow and branching logic
- Note all questions, answers, and their connections
- Identify all video and audio content
- Understand the user journey from start to finish

Return ONLY valid JSON in this exact format (no markdown, no explanations):
{
  "title": "Brief catchy title (max 50 chars)",
  "shortDescription": "Purpose and goal (max 150 chars)",
  "description": "Complete flow description (max 300 chars)"
}`;
};
